import { useEffect, useRef, useState } from 'react';
import type { Deck, Match, Pilot } from '@edh/shared';
import {
  PLACEMENT_GAMES,
  RANKS,
  decksOwnedBy,
  display,
  formatRecord,
  isRanked,
  pilotEloSeries,
  pilotRecord,
  rankFor,
  rankThrough,
} from '@edh/shared';
import { Avatar } from './Avatar';
import { Halo, RankBadge } from './RankBadge';
import { RankGuideSheet } from './RankGuide';

/** Same three the ladder awards, so the card and the row agree. */
const MEDAL = ['gold', 'silver', 'bronze'] as const;
import { useStore } from '../lib/store';

/**
 * Where the standings open: you.
 *
 * A ladder answers "who is ahead" and leaves everyone to find their own row.
 * This answers "how am I doing", which is the question people actually opened
 * the app with — and it is the only place in the app that is about one person
 * rather than the guild.
 *
 * Everything on it is derived from the match history rather than stored, so it
 * cannot drift out of step with the ratings it is describing.
 */
export function YouCard() {
  const { me, state } = useStore();
  if (!me) return null;
  return (
    <YouCardFor pilot={me} matches={state.matches} pilots={state.pilots} decks={state.decks} />
  );
}

function YouCardFor({
  pilot,
  matches,
  pilots,
  decks,
}: {
  pilot: Pilot;
  matches: Match[];
  pilots: Pilot[];
  decks: Deck[];
}) {
  const [guide, setGuide] = useState(false);
  const ranked = isRanked(pilot.placementGames);

  /** Their games, newest first — the basis for every streak below. */
  const mine = [...matches]
    .filter((m) => m.entries.some((e) => e.pilotId === pilot.id))
    .sort((a, b) => b.ts - a.ts);

  /**
   * The longest run of wins they have ever put together.
   *
   * A record rather than a status. The current run was blank more often than
   * not — it is only ever non-zero between a win and the next loss — and a
   * number that spends most of its life as a dash is not worth a slot. This one
   * never goes down.
   */
  let best = 0;
  let run = 0;
  for (const m of [...mine].reverse()) {
    run = m.winnerPilotId === pilot.id ? run + 1 : 0;
    if (run > best) best = run;
  }

  /**
   * The highest this rating has ever been, walked backwards out of the deltas.
   *
   * Not stored on the pilot — decks carry a peak because the spec asked for one,
   * and adding a second stored high would be a number that can disagree with the
   * history. Replaying is cheap at league scale and cannot.
   */
  let elo = pilot.elo;
  let peak = pilot.elo;
  for (const m of mine) {
    elo -= m.deltas.pilots[pilot.id] ?? 0;
    if (elo > peak) peak = elo;
  }

  const rank = ranked
    ? pilots
        .filter((p) => !p.retired && isRanked(p.placementGames))
        .sort((a, b) => b.elo - a.elo)
        .findIndex((p) => p.id === pilot.id) + 1
    : 0;
  const rankedCount = pilots.filter((p) => !p.retired && isRanked(p.placementGames)).length;

  const days = Math.max(1, Math.round((Date.now() - pilot.createdAt) / 86_400_000));
  const record = pilotRecord(matches, pilot.id);
  const built = decksOwnedBy(decks, pilot.id);
  // The rank the path arrives at, which is not always the one the number alone
  // would give — see DEMOTION_GRACE.
  const standing = rankThrough(pilotEloSeries(matches, pilot.id, pilot.elo));
  const title = rankFor(pilot.elo, standing.index);
  const holdAt = title.floor - pilot.elo;

  return (
    <div className="you-card">
      <span className="you-flare" />

      <div className="you-head">
        {/* The halo goes where a halo goes. This is the whole reason the ladder
            is drawn as one: the app was already ringing everybody's face. */}
        <span className="you-face">
          <Avatar name={pilot.name} src={pilot.avatar} size={62} />
          {ranked && (
            <Halo
              style={title.halo}
              color={title.color}
              glow={title.glow && !standing.held}
              held={standing.held}
              className="you-halo"
            />
          )}
          {/* The medal the ladder gives you, on the card that is about you.
              Having to scroll down and find your own row to see it was the
              wrong way round. */}
          {rank > 0 && rank <= 3 && (
            <span className={`rank you-medal ${MEDAL[rank - 1]}`} aria-label={`rank ${rank}`}>
              {rank}
            </span>
          )}
        </span>
        <div className="you-id">
          <div className="you-name">{pilot.name}</div>
          <div className="you-sub">
            {ranked ? (
              <>
                {/* The rank first, the ladder position second. One says how you
                    are doing and the other says who is above you, and only the
                    first of those is about you. */}
                <RankBadge elo={pilot.elo} atIndex={standing.index} held={standing.held} />
                {rank > 0 && (
                  <>
                    <span className="dot">·</span>
                    {/* One span, not three. The parent is a flex row, so the
                        spaces around "of" were their own flex items and got
                        collapsed away — the words ran together. */}
                    <span className="you-place">
                      <span className="you-rank">#{rank}</span> of {rankedCount}
                    </span>
                  </>
                )}
              </>
            ) : (
              'Finding your level'
            )}
            {/* The record, under the title rather than out in the stat row —
                it is a description of the person, like the rank beside it, not
                a measurement of a season. */}
            <span className="dot">·</span>
            <span className="you-record num">{formatRecord(record)}</span>
          </div>
        </div>
        <div className="you-elo">
          {/* The one place the whole ladder can be looked up, put beside the
              number it explains rather than hidden in a line of body text. */}
          <button
            className="you-info"
            onClick={() => setGuide(true)}
            aria-label="What do the ranks mean?"
          >
            i
          </button>
          {/* "2 · played" read as a games count and meant placement progress.
              During placement it says so outright, fraction and all, so there
              is nothing to guess at; afterwards this corner is the rating and
              nothing else. */}
          {ranked ? (
            <>
              <Count to={Math.round(pilot.elo)} />
              <span className="you-elo-label">rating</span>
            </>
          ) : (
            <>
              <span className="you-elo-value num">
                {pilot.placementGames}
                <span className="you-elo-of">/{PLACEMENT_GAMES}</span>
              </span>
              <span className="you-elo-label">placement</span>
            </>
          )}
        </div>
      </div>

      {guide && <RankGuideSheet onClose={() => setGuide(false)} />}

      {/* How far to the next title. The number above says where you are; this
          says how much further, which is the only part anyone can act on. */}
      {ranked && title.next !== null && (
        <div className="you-progress">
          <div className="you-progress-bar">
            <span
              style={{ width: `${title.progress * 100}%`, background: title.color }}
            />
          </div>
          {/* When the rank is only being held on grace, the useful number is
              what it takes to deserve it again, not what the next one costs. */}
          <span className="you-progress-label">
            {standing.held ? (
              <>
                {Math.ceil(holdAt)} to hold{' '}
                <span style={{ color: title.color }}>{title.name}</span>
              </>
            ) : (
              <>
                {Math.max(1, Math.ceil(title.next - pilot.elo))} to{' '}
                <span style={{ color: RANKS[title.index + 1]!.color }}>
                  {RANKS[title.index + 1]!.name}
                </span>
              </>
            )}
          </span>
        </div>
      )}

      {/* Ordered by what somebody actually wants to know about themselves. The
          run they are on is the most volatile and least informative of these,
          so it goes last rather than first. */}
      <div className="you-stats">
        {/* Falls back to the win rate only when no game has a finishing order
            — every game typed in after the fact, where nobody wrote down who
            went out when. Showing a rate there is honest; inventing a finish
            would not be. */}
        {/*
          There was a rate here, and it went through three framings — win rate,
          finish score, average place — each of which needed a sentence of
          explanation to stop it being read wrongly. None of them earned the
          space. The record sits under the name a few pixels away and says the
          same thing without any of the trouble.
        */}
        <Stat value={String(mine.length)} label={mine.length === 1 ? 'game' : 'games'} />
        <Stat value={ranked ? String(display(peak)) : '—'} label="all-time high" />
        <Stat value={`${days}`} label={days === 1 ? 'day in the guild' : 'days in the guild'} />
        <Stat
          value={best > 0 ? String(best) : '—'}
          label="best win streak"
          hot={best >= 3}
        />
        {/* Last in the row, because it is the one number here that is not a
            result — it says what you have brought to the table rather than how
            it went. */}
        <Stat value={String(built)} label={built === 1 ? 'deck built' : 'decks built'} />
      </div>
    </div>
  );
}

function Stat({
  value,
  label,
  sub,
  hot,
}: {
  value: string;
  label: string;
  sub?: string;
  hot?: boolean;
}) {
  return (
    <div className="you-stat">
      <span className={`you-stat-value${hot ? ' hot' : ''}`}>{value}</span>
      <span className="you-stat-label">{label}</span>
      {sub && <span className="you-stat-sub">{sub}</span>}
    </div>
  );
}

/** Counts up on arrival. A rating that appears is data; one that lands is yours. */
function Count({ to }: { to: number }) {
  const [value, setValue] = useState(0);
  const frame = useRef(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(to);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / 900);
      setValue(Math.round(to * (1 - Math.pow(1 - t, 3))));
      if (t < 1) frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [to]);

  return <span className="you-elo-value num">{value}</span>;
}
